import {
  isBoolean,
  isNumber,
  isString,
  isNull,
  isObjectLike
} from './lodashUtils'
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable function-paren-newline */
import { deleteTokenOnFirebaseWithEvent } from './events'

import { getDeviceFingerprint } from './fingerprint'

// Used as fallback to failed responses
export const EMPTY_OBJECT_RESPONSE = {}

// This control how many ms an user must wait to scroll twice in pagination
export const MINIMUM_SCROLL_MS_DELAY = 100

// If no product can be matched through `host`(eg: `comprarveiculos.webmotors.com.br`)
// or path (eg: www.cockpit.com.br/comprar-veiculos). This value will be used as fallback.
// In development its most likely that this is will be the default `productIdentifier`
// sent to the remote API.
export const DEFAULT_PRODUCT_IDENTIFIER = 'cockpit'

// eslint-disable-next-line operator-linebreak
export const DEFAULT_ENVIRONMENT = process.env.CDN_DEFAULT_PRODUCT_ENV || process.env.NODE_ENV || 'development'

// Support default configurations through environment variables whereas needed
export const DEFAULT_PRODUCT_CONFIG = {
  identifier: process.env.CDN_DEFAULT_PRODUCT_IDENTIFIER || DEFAULT_PRODUCT_IDENTIFIER,
  host: process.env.CDN_DEFAULT_PRODUCT_DOMAIN,
  environment: DEFAULT_ENVIRONMENT
}

export const canPerfomScrollEvent = (
  event,
  lastTimestamp = 0,
  debug = false
) => {
  // The first scrolling must be allowed
  if (lastTimestamp === 0) return true

  const lastScrollDelay = event.timeStamp - lastTimestamp

  if (debug) console.log(`lastScrollDelay = ${lastScrollDelay}`)

  return lastScrollDelay >= MINIMUM_SCROLL_MS_DELAY
}

export const hasReachedScrollViewport = (target, ref) => {
  const scrollTarget = Math.abs(ref.offsetHeight - target.offsetHeight)

  return target.scrollTop >= scrollTarget
}

export const getPanelPositionsMetadata = (
  referenceElement,
  targetElement,
  viewport = document.body,
  horizontalPadding = 16
) => {
  // Get the current positions and dimensions of the reference and target elements on the DOM
  const positions = referenceElement.getBoundingClientRect()
  const { offsetWidth: elementWidth = 0 } = targetElement
  const leftPadding = referenceElement.offsetWidth + horizontalPadding

  // Calculate if there's available space BEFORE (to the left) the reference X position
  const canRenderLeft = positions.x - elementWidth > 0

  // Calculate if there's available space AFTER (to the right) the reference X position
  const canRenderRight = positions.x + elementWidth < viewport.offsetWidth

  // Possible "X" position to the left side of the viewport
  const positionLeft = positions.x - elementWidth + leftPadding

  // Possible "X" position to the right side of the viewport
  const positionRight = canRenderRight
    ? positions.x - elementWidth - leftPadding
    : positionLeft

  // We always priorize the "left" position whereas we can, that's because this is
  // the default expected UI whenever it's possible
  const finalXPosition = canRenderLeft ? positionLeft : positionRight
  const finalYPosition = positions.top + referenceElement.offsetHeight

  // Return the calculations metadata, not allowing negative values (outside the viewport)
  return {
    x: Math.max(finalXPosition, 0),
    y: finalYPosition,
    canRenderLeft,
    canRenderRight,
    viewportWidth: viewport.offsetWidth
  }
}

export const getPanelCurrentPositions = (
  bell,
  panel,
  viewport = document.body
) => {
  const canCalculate = !!bell && !!panel
  let panelPositions = { x: 0, y: 0, viewportWidth: viewport.offsetWidth }

  if (canCalculate) {
    panelPositions = getPanelPositionsMetadata(bell, panel, viewport) // prettier-ignore
  }

  return panelPositions
}

// This function detects the `productIdentifier` to be used by using a
// hostname(eg: comprarveiculos.webmotors.com.br)
export const getProductConfigByHost = (currentHost, productsConfig = {}) => {
  const productsList = Object.values(productsConfig).flatMap((config) => config)
  const normalizedHost = currentHost ? currentHost.toString().toLowerCase() : ''

  const productHost = productsList.find((productData) => {
    const { host } = productData || {}

    return host === normalizedHost
  })

  return productHost
}

export const getProductConfigByURL = (currentURL = '', productsConfig = {}) => {
  const productsList = Object.values(productsConfig).flatMap((config) => config)

  const productRegexp = productsList.find((productData) => {
    const { regexp } = productData || {}

    return regexp && regexp.test(currentURL)
  })

  return productRegexp
}

export const getProductConfigByLocation = (
  location = {},
  productsConfig = {}
) => {
  const { host, href } = location || {}

  const productLocation = (getProductConfigByURL(href, productsConfig) || getProductConfigByHost(host, productsConfig)) || DEFAULT_PRODUCT_CONFIG // eslint-disable-line

  return productLocation
}

export const deleteTokenByDeviceId = async (apiClient) => {
  const deviceId = await getDeviceFingerprint()

  return apiClient
    .deleteTokenByDeviceId(deviceId)
    .then((deleteDeviceIdResponse) => {
      console.debug('[CDN DEBUG] deleteTokenByDeviceId response')
      console.debug('[CDN DEBUG]', deleteDeviceIdResponse)

      return deviceId
    })
}

export const deleteToken = (apiClient, token) =>
  apiClient.deleteToken(token).then((deleteResponse) => {
    console.debug('[CDN DEBUG] deleteToken response')
    console.debug('[CDN DEBUG]', deleteResponse)

    return token
  })

export const tryDeleteTokenByDeviceId = (apiClient) =>
  new Promise((resolve) =>
    deleteTokenByDeviceId(apiClient)
      .then(() => resolve(true))
      .catch((error) => {
        return resolve(false)
      })
  )

export const tryDeleteToken = async (apiClient, token) =>
  new Promise((resolve) =>
    deleteToken(apiClient, token)
      .then(() => resolve(true))
      .catch((error) => {
        return resolve(false)
      })
  )

export const deleteAllTokens = (apiClient, firebaseConfig) =>
  new Promise((resolve) =>
    deleteTokenOnFirebaseWithEvent(firebaseConfig).then(async (token) => {
      // First, try to remove all tokens associated with this device
      const firebaseResponse = await tryDeleteTokenByDeviceId(apiClient)

      // If the first request fails, try to at least remove the current token
      if (!firebaseResponse) {
        await tryDeleteToken(apiClient, token)
      }

      // Wait until all requests are sent to be resolved
      return resolve(token)
    })
  )

  export const normalizeText = (text = '') =>{
    return text
      ?.normalize('NFD')
      ?.replace(/[\u0300-\u036f]/g, '')
      ?.replace(/[^a-zA-Z0-9 ]/g, '')
      ?.toLowerCase()
      ?.split(' ')
      ?.join('-')
  }

// eslint-disable-next-line object-curly-newline
export { isBoolean, isNumber, isString, isNull, isObjectLike }
